import { Card } from "@material-ui/core";
import auth0 from "auth0-js";
import { Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Redirect } from "react-router-dom";
import auth0Config from "../../utils/auth0ConfigHelper";
import { shouldRenderCaptcha } from "../../utils/captcha";
import { getLocaleFromCookie } from "../../utils/getLocaleFromCookie";
import LoginFormAlert from "./components/LoginFormAlert";
import LoginFormBanner from "./components/LoginFormBanner";
import LoginFormControls from "./components/LoginFormControls";
import LoginFormHeader from "./components/LoginFormHeader";
import LoginFormInputs from "./components/LoginFormInputs";
import LoginFormLogo from "./components/LoginFormLogo";
import MyWarhammerPreLoginCustomisation from "./components/MyWarhammerPreLoginCustomisation";
import {
  initialValues,
  useHandleSubmit,
  validationSchema,
} from "./components/services";
import SharedDeviceLink from "./components/SharedDeviceLink";

const LoginForm: React.FC = () => {
  const title = auth0Config?.dict.signin.title;
  const { t } = useTranslation();
  const redirectURL = new URL(auth0Config.callbackURL);
  if (redirectURL.host.includes("mywarhammer.com")) {
    redirectURL.searchParams.set("locale", getLocaleFromCookie() ?? "en_??");
  }

  const auth0Params = {
    overrides: {
      __tenant: auth0Config.auth0Tenant,
      __token_issuer: auth0Config.authorizationServer.issuer,
    },
    domain: auth0Config.auth0Domain,
    clientID: auth0Config.clientID,
    redirectUri: redirectURL.toString(),
    responseType: "code",
    ...auth0Config.internalOptions,
  };

  const webAuth = new auth0.WebAuth(auth0Params);
  const [captcha, setCaptcha] = useState<undefined | auth0.Captcha>();

  useEffect(() => {
    const captchaContainer = document.querySelector(".captcha-container");
    if (shouldRenderCaptcha(captchaContainer)) {
      setCaptcha(webAuth.renderCaptcha(captchaContainer as HTMLElement));
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document.querySelector(".captcha-container")]);

  const { handleSubmit } = useHandleSubmit(webAuth, captcha);
  const params = new URLSearchParams(window.location.search);
  const prompt = params.get("prompt");

  if (prompt === "signup") {
    params.delete("prompt");
    const updatedSearch = params.toString();
    window.location.search = updatedSearch;
    return <Redirect to="/signup" />;
  }

  return (
    <Formik
      validationSchema={validationSchema(t)}
      validateOnMount={false}
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, errors, touched }): JSX.Element => (
        <Card>
          <Form name="loginForm">
            {title === "delegated-admin-app" ? (
              <LoginFormLogo />
            ) : (
              <LoginFormBanner />
            )}

            <LoginFormHeader
              title={
                title === "delegated-admin-app"
                  ? t("login.csrTitle")
                  : t("login.title")
              }
            />
            <LoginFormInputs
              preControlCustomisation={
                title === "delegated-admin-app" ? undefined : (
                  <MyWarhammerPreLoginCustomisation />
                )
              }
            />
            <LoginFormControls isSubmitting={isSubmitting} />
            {title !== "delegated-admin-app" && <SharedDeviceLink />}
            {touched.auth0Response && errors.auth0Response && (
              <LoginFormAlert />
            )}
          </Form>
        </Card>
      )}
    </Formik>
  );
};

export default LoginForm;
